import dayjs from 'dayjs';
import type { StatusTone } from '@/components/DataTable/statusChips';
import type { CommandHistoryEntry } from '@/utils/syncStatus';

/**
 * What the player inspector's Command History table shows in each cell. Plain
 * functions, so every rule of the spec can be tested without mounting the table.
 */

const STATE_CHIPS: Record<string, { word: string; tone: StatusTone }> = {
    pending: { word: 'Pending', tone: 'pending' },
    acknowledged: { word: 'Acknowledged', tone: 'good' },
    cleared: { word: 'Cleared', tone: 'neutral' },
    skipped: { word: 'Skipped', tone: 'neutral' },
    removed_without_flush: { word: 'Removed without flush', tone: 'bad' },
};

/** The chip for a command's state. An unknown state shows as sent, in grey. */
export function historyStateChip(state: string): { word: string; tone: StatusTone } {
    return STATE_CHIPS[state] ?? { word: state, tone: 'neutral' };
}

const FIXED_SOURCE_WORDS: Record<string, string> = {
    scheduler: 'Scheduler',
    reconciler: 'Reconciler',
    system: 'System',
};

const UNTRACKED_SOURCE_TITLE = 'Another system pushed this command. Tabasco only saw it being flushed.';

/** Who sent the command, and a hover that explains it when the word alone does not. */
export function historySourceLabel(entry: CommandHistoryEntry): { text: string; title: string | null } {
    const userMatch = /^user:(\d+)$/.exec(entry.source);
    if (userMatch) {
        return { text: entry.source_user_name || `User #${userMatch[1]}`, title: null };
    }
    if (entry.source === 'untracked') {
        return { text: 'Untracked', title: UNTRACKED_SOURCE_TITLE };
    }
    return { text: FIXED_SOURCE_WORDS[entry.source] ?? entry.source, title: null };
}

export type HistoryTimeColumn = 'issued' | 'acknowledged' | 'delivered' | 'done';

/**
 * A time cell. `na` means it will never happen for this command, `waiting` means
 * it has not happened yet, and `unknown` means the row cannot tell which.
 */
export type HistoryTimeCell =
    | { kind: 'time'; short: string; full: string }
    | { kind: 'na' }
    | { kind: 'waiting' }
    | { kind: 'unknown'; title?: string };

const TIME_OF_DAY_FORMAT = 'h:mm A';
const FULL_TIME_FORMAT = 'MM/DD/YYYY h:mm:ss A';

function timeValue(entry: CommandHistoryEntry, column: HistoryTimeColumn): string | null {
    switch (column) {
        case 'issued':
            return entry.issued_at;
        case 'acknowledged':
            return entry.acknowledged_at;
        case 'delivered':
            return entry.delivered_at;
        case 'done':
            return entry.completed_at;
    }
}

/** True while the player has not yet finished with the command. */
function isStillOpen(state: string): boolean {
    return state === 'pending' || state === 'acknowledged';
}

/** Whether an empty time will still happen. Null when the row lacks the flag that says. */
function isStillExpected(entry: CommandHistoryEntry, column: HistoryTimeColumn): boolean | null {
    switch (column) {
        case 'issued':
            return false;
        case 'acknowledged':
            return entry.state === 'pending';
        case 'delivered':
            return typeof entry.delivers_content === 'boolean'
                ? entry.delivers_content && isStillOpen(entry.state)
                : null;
        case 'done':
            return typeof entry.reports_completion === 'boolean'
                ? entry.reports_completion && isStillOpen(entry.state)
                : null;
    }
}

/** What one time cell shows, in the browser's local time. */
export function historyTimeCell(entry: CommandHistoryEntry, column: HistoryTimeColumn): HistoryTimeCell {
    const rawTime = timeValue(entry, column);
    if (rawTime) {
        const parsedTime = dayjs(rawTime);
        if (!parsedTime.isValid()) {
            return { kind: 'unknown', title: rawTime };
        }
        return { kind: 'time', short: parsedTime.format(TIME_OF_DAY_FORMAT), full: parsedTime.format(FULL_TIME_FORMAT) };
    }
    const stillExpected = isStillExpected(entry, column);
    if (stillExpected === null) {
        return { kind: 'unknown' };
    }
    return stillExpected ? { kind: 'waiting' } : { kind: 'na' };
}
